import { useState } from "react";
import { userApi } from "../api/user";
import { UpdateSocialPayload } from "../types/user";

/**
 * Custom React hook to handle updating the user's social links.
 *
 * Provides a status object for loading, error, and success states,
 * a function to clear the status, and a function to send the update request.
 *
 * @returns {{
 *   status: { loading: boolean; error: string | null; success: string | null },
 *   clearStatus: () => void,
 *   send: (data: UpdateSocialPayload) => Promise<void>
 * }}
 */
export function useSocialUpdate() {
  // State to track the status of the update operation
  const [status, setStatus] = useState<{
    loading: boolean;
    error: string | null;
    success: string | null;
  }>({ loading: false, error: null, success: null });

  /**
   * Resets the status state to its initial values.
   */
  const clearStatus = () =>
    setStatus({ loading: false, error: null, success: null });

  /**
   * Sends the update social links request to the API.
   *
   * @param {UpdateSocialPayload} data - The social links data to update.
   * @returns {Promise<void>} Resolves if the update is successful.
   * @throws Will throw an error if the update fails.
   */
  const send = async (data: UpdateSocialPayload) => {
    setStatus({ loading: true, error: null, success: null });
    const fd = new FormData();
    // Append social links to the FormData if present
    if (data.twitter) fd.append("twitter", data.twitter);
    if (data.linkedIn) fd.append("linkedIn", data.linkedIn);
    if (data.instagram) fd.append("instagram", data.instagram);
    try {
      // Call the API to update social links
      await userApi.updateSocial(fd);
      setStatus({
        loading: false,
        error: null,
        success: "Social links updated!",
      });
    } catch (e: any) {
      // Set error status if the API call fails
      setStatus({
        loading: false,
        error: e?.message || "Failed to update social links",
        success: null,
      });
      throw e;
    }
  };

  // Return the status, clearStatus, and send function for use in components
  return { status, clearStatus, send };
}
